import React from 'react';
import { ShieldCheck, FileText, Database, Users, Cookie, Scale, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Componente Reutilizado: PageHeader
const PageHeader = ({ title, subtitle }) => (
    <div className="bg-gray-950 text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
            <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-green-300 to-amber-400"
            >
                {title}
            </motion.h1>
            <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                className="mt-4 max-w-3xl mx-auto text-lg sm:text-xl text-green-100/80"
            >
                {subtitle}
            </motion.p>
        </div>
    </div>
);

// Componente Reutilizado: Footer (simplificado para este exemplo)
const Footer = () => (
    <footer className="bg-gray-950 border-t border-green-800/20 text-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div>
                    <h3 className="text-sm font-semibold text-green-400 tracking-wider uppercase">Soluções</h3>
                    <ul className="mt-4 space-y-4">
                        <li><Link to="/modulos" className="text-base text-gray-300 hover:text-white">Módulos</Link></li>
                        <li><Link to="/#precos" className="text-base text-gray-300 hover:text-white">Preços</Link></li>
                    </ul>
                </div>
                <div>
                    <h3 className="text-sm font-semibold text-green-400 tracking-wider uppercase">Empresa</h3>
                    <ul className="mt-4 space-y-4">
                        <li><Link to="/sobre" className="text-base text-gray-300 hover:text-white">Sobre Nós</Link></li>
                        <li><Link to="/contato" className="text-base text-gray-300 hover:text-white">Contato</Link></li>
                    </ul>
                </div>
                <div>
                    <h3 className="text-sm font-semibold text-green-400 tracking-wider uppercase">Legal</h3>
                    <ul className="mt-4 space-y-4">
                        <li><Link to="/privacidade" className="text-base text-gray-300 hover:text-white">Privacidade</Link></li>
                        <li><Link to="/seguranca" className="text-base text-gray-300 hover:text-white">Segurança</Link></li>
                    </ul>
                </div>
                <div className="md:col-span-1">
                     <h3 className="text-sm font-semibold text-green-400 tracking-wider uppercase">AgroGestão</h3>
                     <p className="mt-4 text-base text-gray-300">Transformando a gestão do agronegócio com tecnologia e dados.</p>
                </div>
            </div>
            <div className="mt-8 border-t border-green-800/20 pt-8 md:flex md:items-center md:justify-between">
                <p className="mt-8 text-base text-gray-400 md:mt-0 md:order-1">&copy; {new Date().getFullYear()} AgroGestão. Todos os direitos reservados.</p>
            </div>
        </div>
    </footer>
);

const PrivacySection = () => {
    const policies = [
        {
            icon: <FileText className="w-10 h-10 text-amber-400" />,
            title: "Coleta de Dados",
            description: "Coletamos informações que você nos fornece diretamente (cadastro, contato) e dados gerados durante o uso da plataforma (dados de produção, financeiros) para operar e melhorar nossos serviços."
        },
        {
            icon: <Database className="w-10 h-10 text-amber-400" />,
            title: "Uso das Informações",
            description: "Utilizamos seus dados para personalizar sua experiência, fornecer suporte, processar transações, e para fins de análise e melhoria contínua do AgroGestão. Jamais vendemos seus dados para terceiros."
        },
        {
            icon: <Users className="w-10 h-10 text-amber-400" />,
            title: "Compartilhamento de Dados",
            description: "Podemos compartilhar dados com parceiros de serviço (ex: processadores de pagamento, infraestrutura em nuvem) sob estritas cláusulas de confidencialidade, ou quando exigido por lei."
        },
        {
            icon: <ShieldCheck className="w-10 h-10 text-amber-400" />,
            title: "Segurança e Proteção",
            description: "Empregamos medidas de segurança de ponta, como criptografia de ponta-a-ponta e controle de acesso rigoroso, para proteger suas informações contra acesso, alteração ou destruição não autorizada."
        },
        {
            icon: <Scale className="w-10 h-10 text-amber-400" />,
            title: "Seus Direitos (LGPD)",
            description: "Você tem o direito de acessar, corrigir, excluir ou solicitar a portabilidade de seus dados. Oferecemos ferramentas na plataforma para que você exerça seu controle de forma simples e direta."
        },
        {
            icon: <Cookie className="w-10 h-10 text-amber-400" />,
            title: "Cookies e Tecnologias",
            description: "Usamos cookies essenciais para o funcionamento da plataforma e cookies de análise (de forma anônima) para entender como nosso serviço é utilizado e como podemos melhorá-lo."
        }
    ];

    return (
        <div className="bg-gray-950 py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-2xl mx-auto lg:text-center">
                    <h2 className="text-base font-semibold leading-7 text-amber-400">Nosso Compromisso</h2>
                    <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Transparência e Controle</p>
                    <p className="mt-6 text-lg leading-8 text-gray-300">
                        Nossa Política de Privacidade foi criada para ser clara e acessível. Queremos que você entenda exatamente como seus dados são tratados e tenha total controle sobre suas informações.
                    </p>
                </div>
                <div className="mt-16 max-w-2xl mx-auto sm:mt-20 lg:mt-24 lg:max-w-none">
                    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                        {policies.map((policy, index) => (
                            <motion.div 
                                key={index}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                                className="flex flex-col bg-green-900/20 border border-green-800/30 rounded-lg p-8 h-full"
                            >
                                <div className="mb-4">{policy.icon}</div>
                                <h3 className="text-xl font-bold text-white mb-3">{policy.title}</h3>
                                <p className="text-green-100/80 flex-grow">{policy.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Componente Reutilizado: CTA
const CtaPrivacidade = () => (
    <div className="bg-gray-900">
        <div className="max-w-4xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                <span className="block">Dúvidas sobre nossa política?</span>
            </h2>
            <p className="mt-4 text-lg leading-6 text-green-100/80">
                Nossa equipe de suporte está pronta para esclarecer qualquer questão sobre como protegemos e gerenciamos seus dados.
            </p>
            <Link
                to="/contato"
                className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-green-950 bg-amber-400 hover:bg-amber-500 sm:w-auto"
            >
                Fale Conosco
                <ArrowRight className="ml-2 -mr-1 h-5 w-5" />
            </Link>
        </div>
    </div>
);

export default function Privacidade() {
    return (
        <div className="bg-gray-950 min-h-screen">
            <PageHeader 
                title="Política de Privacidade"
                subtitle="Seu controle e a segurança dos seus dados são nossa prioridade máxima."
            />
            <main>
                <PrivacySection />
                <CtaPrivacidade />
            </main>
            <Footer />
        </div>
    );
}
